import styles from '../App.module.css';
import { MascotRow } from './MascotRow';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';

interface HeroProps {
  overallProgress: number;
  isDark: boolean;
  onToggleTheme: () => void;
  snapshotLabel?: string;
}

function percent(p: number): string {
  return `${Math.round(p * 100)}%`;
}

export function Hero({ overallProgress, isDark, onToggleTheme, snapshotLabel }: HeroProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const r = 22;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - overallProgress);

  return (
    <header className={styles.hero}>
      <div className={styles.heroGradient} aria-hidden="true" />
      <div className={styles.heroInner}>
        <button
          type="button"
          className={styles.themeToggle}
          onClick={onToggleTheme}
          aria-label={isDark ? ui.hero.lightMode : ui.hero.darkMode}
        >
          {isDark ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        <p className={styles.heroEyebrow}>{ui.hero.eyebrow}</p>
        <h1 className={styles.heroTitle}>{ui.hero.title}</h1>
        <p className={styles.heroTagline}>{ui.hero.tagline}</p>
        {snapshotLabel ? <p className={styles.heroSnapshot}>{snapshotLabel}</p> : null}
        <MascotRow
          className={styles.heroMascots}
          items={[
            { id: 'star', size: 52 },
            { id: 'haha', size: 64 },
            { id: 'momo', size: 96 },
            { id: 'papa', size: 64 },
            { id: 'sleep', size: 52 },
          ]}
        />
        <div className={styles.heroProgress}>
          <div className={styles.progressRing} aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle
                className={styles.progressRingBg}
                cx="28" cy="28" r={r}
              />
              <circle
                className={styles.progressRingFill}
                cx="28" cy="28" r={r}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
              />
            </svg>
            <span className={styles.progressRingValue}>{percent(overallProgress)}</span>
          </div>
          <span className={styles.heroProgressLabel}>{ui.hero.overallProgress}</span>
        </div>
      </div>
    </header>
  );
}
